import {
  ChartProps,
  EquipmentData,
  FilterData,
  SimpleEquipmentData,
} from './types';
import ecStat from 'echarts-stat';
import { colorPalette, mockData } from './mock';
import * as echarts from 'echarts';
import { EChartsCoreOption, EChartsOption, SeriesOption } from 'echarts';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useCallback, useState } from 'react';
import {
  calculateCategoryIndex,
  extractUniqueValues,
  transformArray,
  sortKeys,
} from './utils';

const ecStatLocal: any = ecStat;

const median = (arr: number[]): number => {
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

export const useCharts = ({
  charts,
  chartsRef,
  chartRefs,
  receivedCharts,
}: any) => {
  const [chartsDropdownData, setChartsDropdownData] = useState<{
    [key: string]: any;
  }>({});

  const [chartsDropdownDataFiltered, setChartsDropdownDataFiltered] =
    useState<any>();

  const [previousData, setPreviousData] = useState<FilterData>({
    '': [[], [], ''],
  });

  const filterResponse = (filterSettings: any, separatedData: any) => {
    const extractedFilterSettings: string[] = [];
    const filteredData: any[] = [];

    Object.entries(filterSettings).forEach((elem: any) => {
      extractedFilterSettings.push(elem[0].split('-')[0]);
      extractedFilterSettings.push(elem[1]);
    });
    const uniqFilters = extractedFilterSettings;
    const uniqFiltersArray = transformArray(uniqFilters);

    Object.entries(separatedData).forEach((currentChartData: any) => {
      if (
        uniqFilters.length === 0 ||
        uniqFilters.indexOf(currentChartData[0]) < 0
      ) {
        filteredData.push(currentChartData[1]);
      } else {
        const collectArray: any[] = [];
        const arr = uniqFiltersArray.filter(
          (filter) => filter.indexOf(currentChartData[0]) >= 0,
        );

        currentChartData[1].forEach((el: (string | string[])[]) => {
          let matchesAllFilters: boolean[] = [];
          arr.flat().forEach((filterItem) => {
            if (filterItem === currentChartData[0]) {
              matchesAllFilters.push(true);
            }
            if (
              el[0].indexOf(filterItem) >= 0 ||
              el[0].indexOf(filterItem.toString()) >= 0
            ) {
              matchesAllFilters.push(true);
            } else {
              matchesAllFilters.push(false);
            }
          });

          if (matchesAllFilters.indexOf(false) === -1) {
            collectArray.push(el);
          }
        });

        if (collectArray.length) {
          filteredData.push(collectArray);
        } else {
          filteredData.push(currentChartData[1]);
        }
      }
    });

    const finalArray = Array.from(new Set(filteredData.flat()));
    let finalObject = {} as { [p: string]: any };

    finalArray.forEach((element) => {
      if (!finalObject[element[0][3]]) {
        finalObject[element[0][3]] = [];
      }
      finalObject[element[0][3]].push(element);
    });
    return finalObject;
  };

  const getUpdatedChartsSettings = useCallback(
    (charts: [string, unknown][], trigger = true, filtersSettings: any) => {
      let updatedChartsSettings: any = [];
      let reorderCharts = [];
      reorderCharts[0] = charts[1];
      reorderCharts[1] = charts[0];
      reorderCharts[2] = charts[2];
      reorderCharts &&
        reorderCharts.length > 0 &&
        reorderCharts.forEach((entry) => {
          const chartInfo = entry[1] as ChartProps;
          const chartData = chartInfo.data;
          let chartDataFilteredBySurvey = {} as { [key: string]: any };
          if (entry[0] === 'EmissionRateFrequencyRed') {
            chartData.forEach((element: (string | number)[][]) => {
              if (!element[0][3]) {
                return;
              }
              if (!chartDataFilteredBySurvey[element[0][3]]) {
                chartDataFilteredBySurvey[element[0][3]] = [];
                chartDataFilteredBySurvey[element[0][3]].push(element);
              } else {
                chartDataFilteredBySurvey[element[0][3]].push(element);
              }
            });

            const mergedChartData: { [p: string]: any[][] } = {};

            const data: any[][][] = Object.entries(
              chartDataFilteredBySurvey,
            )[0];

            const resultDict: { [key: string]: { [key: string]: number[] } } =
              {};

            if (!data?.length) {
              return;
            }

            data[1].forEach((item: any[][]) => {
              const firstSubElement = item[0][0];
              const thirdSubElement = item[0][2];
              const lastSubElement = item[0][3];

              if (!resultDict[lastSubElement]) {
                resultDict[lastSubElement] = {};
              }

              if (resultDict[lastSubElement][firstSubElement]) {
                resultDict[lastSubElement][firstSubElement].push(
                  thirdSubElement,
                );
              } else {
                resultDict[lastSubElement][firstSubElement] = [thirdSubElement];
              }
            });

            const resultArray = Object.entries(resultDict).map(
              ([key, values]) => [
                key,
                Object.entries(values).map(([key, values]) => [key, values]),
              ],
            );

            setChartsDropdownDataFiltered(resultArray);

            const processedChartData = filterResponse(
              filtersSettings,
              chartDataFilteredBySurvey,
            );

            Object.entries(processedChartData).forEach((data) => {
              const initialArray = data[1];
              mergedChartData[data[0]] = [
                [
                  // Combine the first elements of each sub-array
                  initialArray
                    .map((subArray: any[]) => subArray[0])
                    .reduce(
                      (
                        acc: { [x: string]: { add: (arg0: any) => void } },
                        curr: any[],
                      ) => {
                        curr.forEach((num, index) => {
                          if (!acc[index]) acc[index] = new Set();
                          acc[index].add(num);
                        });
                        return acc;
                      },
                      [],
                    )
                    .map((set: Iterable<unknown> | ArrayLike<unknown>) =>
                      Array.from(set),
                    ),
                  // Flatten and merge the second elements of each sub-array
                  initialArray.flatMap((subArray: any[]) => subArray[1]),
                  // Use the median from the first element
                  initialArray[0] && initialArray[0][2],
                ],
              ];
            });
            chartDataFilteredBySurvey = mergedChartData;

            const uniqueValues = extractUniqueValues(chartDataFilteredBySurvey);
            if (trigger) {
              setChartsDropdownData((prevState) => {
                const newState = { ...prevState };
                newState[entry[0]] = uniqueValues;
                return newState;
              });
            }
          } else {
            const uniqueValues = extractUniqueValues(chartDataFilteredBySurvey);
            setChartsDropdownData((prevState) => {
              const newState = { ...prevState };
              newState[entry[0]] = uniqueValues;
              return newState;
            });
            chartDataFilteredBySurvey = { chartData };
          }
          const dataObj: FilterData = {};
          try {
            Object.entries(sortKeys(chartDataFilteredBySurvey)).forEach(
              (chartItems: any) => {
                chartItems[1].forEach((chartItem: any) => {
                  const currentMeasurement = JSON.parse(
                    JSON.stringify(chartItems),
                  )[0];
                  let filteredData = mockData.charts.filter(
                    (el) => el.id === chartInfo.id,
                  );

                  const existingChartData = filteredData.length
                    ? JSON.parse(JSON.stringify(filteredData[0]))
                    : null;
                  if (!chartItem[1] || chartItem[1].length === 0) {
                    if (
                      'name' in existingChartData['xAxis'] &&
                      chartInfo['x_label']
                    ) {
                      existingChartData['xAxis']['name'] = chartInfo.x_label;
                    }
                    if (
                      'name' in existingChartData['yAxis'] &&
                      chartInfo['y_label']
                    ) {
                      existingChartData['yAxis']['name'] = chartInfo['y_label'];
                    }
                    existingChartData['xAxis']['data'] = null;
                    existingChartData['series'][0]['data'] = [
                      // ...previousData[currentMeasurement][1],
                    ];
                    let additionalTitle = '';

                    existingChartData['title']['text'] =
                      existingChartData['title']['text'] +
                      ': ' +
                      additionalTitle;

                    existingChartData['measurement'] =
                      previousData[currentMeasurement][2];

                    updatedChartsSettings.push(existingChartData);
                  } else {
                    const bins = ecStat.histogram(chartItem[1], 'sturges');

                    const xData = bins.data.map(function (bin) {
                      return bin[0];
                    });
                    const yData = bins.data.map(function (bin) {
                      return bin[1];
                    });

                    if (existingChartData) {
                      if ('data' in existingChartData['xAxis']) {
                        existingChartData['xAxis']['data'] = xData;
                      }
                      if (
                        'name' in existingChartData['xAxis'] &&
                        chartInfo['x_label']
                      ) {
                        existingChartData['xAxis']['name'] = chartInfo.x_label;
                      }
                      if (
                        'name' in existingChartData['yAxis'] &&
                        chartInfo['y_label']
                      ) {
                        existingChartData['yAxis']['name'] =
                          chartInfo['y_label'];
                      }
                      if (
                        'markLine' in existingChartData['series'][0] &&
                        chartItem[1]
                      ) {
                        if (
                          'xAxis' in
                          existingChartData['series'][0]['markLine']['data'][0]
                        ) {
                          existingChartData['series'][0]['markLine'][
                            'data'
                          ][0].xAxis = calculateCategoryIndex(
                            xData,
                            median(chartItem[1]),
                          );
                        }
                      }
                      if (
                        'markLine' in existingChartData['series'][0] &&
                        chartItem[3]
                      ) {
                        if (
                          'xAxis' in
                          existingChartData['series'][0]['markLine']['data'][0]
                        ) {
                          const newMarkline = JSON.parse(
                            JSON.stringify(
                              existingChartData['series'][0]['markLine'][
                                'data'
                              ][0],
                            ),
                          );
                          existingChartData['series'][0]['markLine'][
                            'data'
                          ].push(newMarkline);
                          existingChartData['series'][0]['markLine']['data'][1][
                            'xAxis'
                          ] = calculateCategoryIndex(xData, chartItem[3][1]);
                        }
                      }

                      if (
                        'markLine' in existingChartData['series'][0] &&
                        chartItem[1]
                      ) {
                        if (
                          'label' in
                          existingChartData['series'][0]['markLine']['data'][0]
                        ) {
                          existingChartData['series'][0]['markLine']['data'][0][
                            'label'
                          ]['formatter'] =
                            'Median: ' +
                            median(chartItem[1])
                              .toFixed(
                                chartInfo.x_label.includes('SCFH')
                                  ? 0
                                  : chartInfo.x_label.includes('Intensity')
                                    ? 2
                                    : 1,
                              )
                              .toString();
                        }
                      }

                      if (
                        'markLine' in existingChartData['series'][0] &&
                        chartItem[3]
                      ) {
                        if (
                          'label' in
                          existingChartData['series'][0]['markLine']['data'][1]
                        ) {
                          existingChartData['series'][0]['markLine']['data'][1][
                            'label'
                          ]['formatter'] = chartItem[3][0];
                          existingChartData['series'][0]['markLine']['data'][1][
                            'label'
                          ]['distance'] = [0, 20];
                        }
                      }

                      existingChartData['series'][0]['data'] = [...yData];

                      const currentMeasurement = JSON.parse(
                        JSON.stringify(chartItem),
                      )[0][3];

                      dataObj[currentMeasurement] = [
                        xData,
                        yData,
                        currentMeasurement,
                      ];
                      setPreviousData((prevState: any) => {
                        return { ...prevState, ...dataObj };
                      });
                      let additionalTitle: (string | any[])[][] = [];
                      const dataForChartTitle =
                        chartItem[0].length > 2
                          ? chartItem[0].slice(3, 4)
                          : [chartItem[0].slice(0, 1)];
                      dataForChartTitle.forEach((el: string | any[]) => {
                        if (el.length === 1) {
                          additionalTitle.push([el]);
                        }
                      });

                      existingChartData['title']['text'] =
                        existingChartData['title']['text'] +
                        ': ' +
                        additionalTitle.join(' ');

                      existingChartData['measurement'] = currentMeasurement;

                      updatedChartsSettings.push(existingChartData);
                    }
                  }
                });
              },
            );
          } catch (e) {
            console.log(e);
          }
        });

      return updatedChartsSettings;
    },
    [previousData],
  );

  const getUpdatedCharts = useCallback(() => {
    return chartsRef.current.map(
      (
        chart: { chart: echarts.ECharts; dom: HTMLElement | null | undefined },
        index: string | number,
      ) => {
        if (chartRefs.current[index]) {
          if (chart.chart) {
            chart.chart.dispose();
          }
          chart.dom = chartRefs.current[index];
          // Check if an ECharts instance already exists on the DOM element
          const existingChart =
            chart.dom && echarts.getInstanceByDom(chart.dom);
          if (existingChart) {
            // If an instance exists, dispose of it
            existingChart.dispose();
          }
          if (
            chart.dom &&
            chart.dom.clientWidth !== 0 &&
            chart.dom.clientHeight !== 0
          ) {
            setTimeout(() => {
              chart.chart = echarts.init(chart.dom);
              echarts.registerTransform(ecStatLocal.transform.regression);
              chart.chart.setOption(receivedCharts[index] as EChartsOption);
            }, 500);
          }
        }
        return chart;
      },
    );
  }, [chartRefs, chartsRef, receivedCharts]);

  const handleDownloadChart = (index: number) => {
    const chart = charts[index]?.chart;
    if (chart) {
      const url = chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
      });
      const link = document.createElement('a');
      link.href = url;
      link.download = `chart-${index}.png`;
      link.click();
    }
  };

  const handleDownloadQuadCharts = (startIndex: number, endIndex: number) => {
    const zip = new JSZip();
    const folder = zip.folder('quad-charts')!;

    for (let i = startIndex; i <= endIndex; i++) {
      const chart = charts[i]?.chart;
      if (chart) {
        const url = chart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        });
        const base64Data = url.split(',')[1];
        folder.file(`chart-${i}.png`, base64Data, { base64: true });
      }
    }

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, 'quad-charts.zip');
    });
  };

  const handleChartsSequenceDownload = () => {
    const zip = new JSZip();
    const folder = zip.folder('chart-images')!;
    const dataFolder = zip.folder('chart-data')!;

    charts.forEach(
      (
        chart: {
          chart: {
            getDataURL: (arg0: {
              type: string;
              pixelRatio: number;
              backgroundColor: string;
            }) => any;
            getOption: () => echarts.EChartsCoreOption;
          };
        },
        i: any,
      ) => {
        if (chart.chart) {
          const url = chart.chart.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          });
          const base64Data = url.split(',')[1];
          folder.file(`chart-${i}.png`, base64Data, { base64: true });

          const chartOption = chart.chart.getOption() as EChartsCoreOption;
          const seriesOptions = chartOption.series as SeriesOption[];

          const xAxisData =
            chartOption.xAxis && Array.isArray(chartOption.xAxis)
              ? chartOption.xAxis[0].data
              : [];

          const yAxisData = seriesOptions.map((series) => series.data);

          const chartData = {
            xAxis: xAxisData,
            yAxis: yAxisData,
          };

          const jsonData = JSON.stringify(chartData, null, 2);
          dataFolder.file(`chart-${i}.json`, jsonData);
        }
      },
    );

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, 'primary-charts.zip');
    });
  };

  const colors = Object.values(colorPalette.primary);

  const addM2BasinCharts = (
    chartdata: EquipmentData,
  ): echarts.EChartsOption => {
    const categories: string[] = [];
    const totalData: { value: number; name: string }[] = [];
    let totalSum = 0;

    for (const [key, values] of Object.entries(chartdata)) {
      categories.push(key);
      const leak = values['Average Leak (kg)'];
      const normal = values['Average Normal (kg)'];
      const total = leak + normal;
      totalData.push({ value: total, name: key });
      totalSum += total;
    }

    const option: echarts.EChartsOption = {
      title: {
        text: 'Measured Emission',
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const realValue =
            totalData.find((item) => item.name === params.seriesName)?.value ||
            0;
          return `${params.seriesName}: ${realValue.toFixed(1)} (${params.value}%)`;
        },
      },
      legend: {
        data: categories,
      },
      xAxis: {
        type: 'category',
        data: ['Measured Emission'],
      },
      yAxis: [
        {
          type: 'value',
          name: '%',
          min: 0,
          max: 100,
          interval: 20,
          position: 'left',
          axisLabel: {
            formatter: '{value} %',
          },
        },
        {
          type: 'value',
          name: 'Total Emissions, kg',
          position: 'right',
          min: 0,
          max: totalSum,
          interval: totalSum / 5,
          axisLabel: {
            formatter: (value: number) => `${value.toFixed(1)} kg`,
          },
        },
      ],
      chart_type: 'primary',
      series: totalData.map((item, index) => ({
        name: item.name,
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          position: 'inside',
          formatter: '{c} %',
        },
        data: [
          {
            value: parseFloat(((item.value / totalSum) * 100).toFixed(1)),
            realValue: parseFloat(item.value.toFixed(1)),
          },
        ],
        itemStyle: {
          color: colors[index % colors.length],
        },
      })),
    };

    return option;
  };

  const addM2EpisodicCharts = (data: number[]): echarts.EChartsOption => {
    const totalSum = data.reduce((sum: any, value: any) => sum + value, 0);

    const option: echarts.EChartsCoreOption = {
      title: {
        text: 'Episodic Events',
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const realValue = data[params.dataIndex as number];
          const percentage =
            params.value !== null && params.value !== undefined
              ? params.value.toFixed(1)
              : '0.00';
          return `Value: ${realValue.toFixed(1)} (${percentage}%)`;
        },
      },
      chart_type: 'primary',
      xAxis: {
        type: 'category',
        data: ['Episodic events'],
      },
      yAxis: [
        {
          type: 'value',
          name: '%',
          min: 0,
          max: 100,
          interval: 20,
          position: 'left',
          axisLabel: {
            formatter: '{value} %',
          },
        },
        {
          type: 'value',
          name: 'Total Emissions, kg',
          position: 'right',
          min: 0,
          max: totalSum,
          interval: totalSum / 5,
          axisLabel: {
            formatter: (value: number) => `${value.toFixed(1)} kg`,
          },
        },
      ],
      series: data.map((value, index) => ({
        name: `Segment ${index + 1}`,
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          position: 'inside',
          formatter: (params: any) => {
            const percentage =
              params.value !== null && params.value !== undefined
                ? params.value.toFixed(1)
                : '0.00';
            return `${percentage} %`;
          },
        },
        data: [
          {
            value: parseFloat(((value / totalSum) * 100).toFixed(1)),
            realValue: parseFloat(value.toFixed(1)),
          },
        ],
        itemStyle: {
          color: colors[index % colors.length],
        },
      })),
    };

    return option as EChartsOption;
  };

  const addM2DLCharts = (
    chartdata: SimpleEquipmentData,
  ): echarts.EChartsOption => {
    const categories: string[] = [];
    const totalData: { value: number; name: string }[] = [];
    let totalSum = 0;

    for (const [key, values] of Object.entries(chartdata)) {
      categories.push(key);
      const total = values.reduce((sum: any, value: any) => sum + value, 0);
      totalData.push({ value: total, name: key });
      totalSum += total;
    }

    const option: echarts.EChartsCoreOption = {
      title: {
        text: '<DL Emissions',
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const realValue =
            totalData.find((item) => item.name === params.seriesName)?.value ||
            0;
          return `${params.seriesName}: ${realValue.toFixed(1)} (${params.value}%)`;
        },
      },
      legend: {
        data: categories,
      },
      xAxis: {
        type: 'category',
        data: ['<DL Emissions'],
      },
      yAxis: [
        {
          type: 'value',
          name: '%',
          min: 0,
          max: 100,
          interval: 20,
          position: 'left',
          axisLabel: {
            formatter: '{value} %',
          },
        },
        {
          type: 'value',
          name: 'Total Emissions, kg',
          position: 'right',
          min: 0,
          max: totalSum,
          interval: totalSum / 5,
          axisLabel: {
            formatter: (value: number) => `${value.toFixed(1)} kg`,
          },
        },
      ],
      chart_type: 'primary',
      series: totalData.map((item, index) => ({
        name: item.name,
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          position: 'inside',
          formatter: '{c} %',
        },
        data: [
          {
            value: parseFloat(((item.value / totalSum) * 100).toFixed(1)),
            realValue: parseFloat(item.value.toFixed(1)),
          },
        ],
        itemStyle: {
          color: colors[index % colors.length],
        },
      })),
    };

    return option as EChartsOption;
  };

  return {
    handleChartsSequenceDownload,
    handleDownloadChart,
    handleDownloadQuadCharts,
    getUpdatedChartsSettings,
    getUpdatedCharts,
    chartsDropdownData,
    chartsDropdownDataFiltered,
    addM2BasinCharts,
    addM2EpisodicCharts,
    addM2DLCharts,
  };
};
